import Accordion from 'components/Accordion';
import DetailsViewCardApplicationList from 'components/DetailsViewCardApplicationList';
import DetailsViewCardHeader from 'components/DetailsViewCardHeader';
import DetailsViewEntityLink from 'components/DetailsViewEntityLink';
import DetailsViewEntityLinkContainer from 'components/DetailsViewEntityLinkContainer';
import DetailsViewPageContainer from 'components/DetailsViewPageContainer';
import SourceBadge from 'components/SourceBadge';
import DetailsViewCardApplicationsListContainer from 'components/containers/DetailsViewCardApplicationsListContainer';
import UpdateBoundaryForm from 'components/forms/UpdateBoundaryForm';

import useData from './hooks/useData';
import useTranslation from './hooks/useTranslation';

import { CARD_HEADER_DETAIL_TYPE, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

const BoundaryDetailPage: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const {
        applications,
        currentBoundary,
        dropdownMenuItems,
        entitiesLink,
        handleClose,
        handleEditClose,
        handleEditMapClick,
        handleEditMapClose,
        handleMapComplete,
        handleSelectApplication,
        handleToggleApplications,
        handleToggleLinkedEntities,
        isApplicationsLoading,
        isApplicationsOpen,
        isClosing,
        isEditingForm,
        isLinkedEntitiesOpen,
        isLoading,
        mapInteractionOptions,
        mapOptions,
        selectedApplicationId,
        setIsClosing
    } = useData();

    const { APPLICATIONS, LINKED_ENTITIES } = useTranslation();

    const { hasDrawingControls } = mapInteractionOptions;

    return (
        <DetailsViewPageContainer
            geometry={{
                isEditable: true,
                parts: currentBoundary?.boundaryParts
            }}
            handleClose={handleClose}
            handleEditMapClick={handleEditMapClick}
            handleEditMapClose={handleEditMapClose}
            handleMapComplete={handleMapComplete}
            hasDrawingControls={hasDrawingControls}
            mapInteractionOptions={mapInteractionOptions}
            mapOptions={mapOptions}
            testId={testId}>
            <DetailsViewCardHeader
                detailType={CARD_HEADER_DETAIL_TYPE}
                isLoading={isLoading}
                items={dropdownMenuItems}
                name={currentBoundary?.name ?? ''}
            />

            <SourceBadge name={currentBoundary?.name ?? ''} />

            {isEditingForm ? (
                <UpdateBoundaryForm
                    formValues={currentBoundary}
                    isClosing={isClosing}
                    onClose={handleEditClose}
                    setIsClosing={setIsClosing}
                />
            ) : (
                <>
                    <Accordion
                        isOpen={isLinkedEntitiesOpen}
                        label={LINKED_ENTITIES}
                        onToggle={handleToggleLinkedEntities}>
                        <DetailsViewEntityLinkContainer>
                            {entitiesLink.map(({ entityName, entityRoute, entityType }) => (
                                <DetailsViewEntityLink
                                    entityName={entityName}
                                    entityRoute={entityRoute}
                                    entityType={entityType}
                                    key={entityRoute}
                                />
                            ))}
                        </DetailsViewEntityLinkContainer>
                    </Accordion>

                    <Accordion
                        isOpen={isApplicationsOpen}
                        label={APPLICATIONS}
                        onToggle={handleToggleApplications}>
                        <DetailsViewCardApplicationsListContainer>
                            <DetailsViewCardApplicationList
                                applications={applications}
                                isLoading={isApplicationsLoading}
                                onSelectApplication={handleSelectApplication}
                                selectedApplicationId={selectedApplicationId}
                            />
                        </DetailsViewCardApplicationsListContainer>
                    </Accordion>
                </>
            )}
        </DetailsViewPageContainer>
    );
};

export default BoundaryDetailPage;
